"use client";

/**
 * MobileSearchSheet — slide-down search overlay used on mobile.
 *
 * Mirrors `guydeez_claude_design/components/20-mobile-shared.jsx` (the search
 * overlay inside MobileHeader). Opened from two entry points:
 *  - the homepage hero's destination card + "Trouver mon guide" button
 *  - the header search icon (SearchTrigger.tsx) on mobile viewports
 *
 * Cities for the "RECHERCHES POPULAIRES" list come from Redux
 * (`state.cities.featured`, hydrated by the homepage SSR or by any other page
 * that loads featured cities). When called from a page that hasn't populated
 * that slice yet the popular list just stays empty — the input still works
 * and routes to /continents on submit.
 */

import React, { FC, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Link from "next/link";
import { useParams, useRouter } from "next/navigation";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "@/app/lib/hooks";
import type { City } from "@/data/types";

interface AutocompleteCity {
  id: number;
  name: string;
  nameTranslated?: string;
  totalTours?: number;
}

interface AutocompleteCountry {
  id: number;
  name: string;
  nameTranslated?: string;
  slug: string;
  city: AutocompleteCity[];
}

interface FlatRow {
  cityId: number;
  cityName: string;
  cityDisplay: string;
  countryName: string;
  countryDisplay: string;
  countrySlug: string;
  totalTours: number;
}

const normalize = (s: string) =>
  (s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

interface Props {
  onClose: () => void;
}

// Filled-marker pin SVG — coral teardrop with a cream-deep inner dot.
// Replaces the 📍 emoji which renders inconsistently across platforms
// (outline on Windows/Linux, filled-flat on iOS/Android).
const PinIcon: FC<{ size?: number }> = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden
  >
    <path
      d="M12 2C7.6 2 4 5.6 4 10c0 5.5 6.6 11.3 7.3 11.9.4.3.9.3 1.3 0 .7-.6 7.4-6.4 7.4-11.9 0-4.4-3.6-8-8-8z"
      fill="var(--g-coral)"
    />
    <circle cx="12" cy="10" r="2.6" fill="var(--g-cream-deep)" />
  </svg>
);

// CMS slugs / DB names sometimes arrive lowercased ("paris", "new york"); we
// title-case for display only, keeping the source data untouched.
const titleCase = (s?: string): string => {
  if (!s) return "";
  return s
    .split(" ")
    .map((w) =>
      w
        .split("-")
        .map((p) => (p ? p[0].toUpperCase() + p.slice(1) : ""))
        .join("-"),
    )
    .join(" ");
};

const MobileSearchSheet: FC<Props> = ({ onClose }) => {
  const { t } = useTranslation("guydeez");
  const { lang, currency } = useParams<{ lang: string; currency: string }>();
  const router = useRouter();
  const selectedLanguage =
    useAppSelector((s: any) => s?.language?.selectedLanguage) ||
    (lang ? String(lang) : "fr_FR");
  const cities: City[] = useAppSelector((s: any) => s?.cities?.featured) || [];

  const [query, setQuery] = useState("");
  const [allRows, setAllRows] = useState<FlatRow[]>([]);

  // Lock body scroll while open + close on ESC.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  // Pull the full cities-by-country dataset (same endpoint the legacy
  // AutoComplete uses) so typing matches against every activated city in
  // the catalogue, not just the SSR-hydrated featured list.
  useEffect(() => {
    let cancelled = false;
    const base = process.env.NEXT_PUBLIC_SOCKET || "";
    if (!base) return;
    (async () => {
      try {
        const { data } = await axios.get(`${base}/api/citiesPerCountry`, {
          params: { lang: selectedLanguage },
        });
        if (cancelled || !Array.isArray(data)) return;
        const flat: FlatRow[] = (data as AutocompleteCountry[]).flatMap((co) =>
          (co.city || []).map((ci) => ({
            cityId: ci.id,
            cityName: ci.name,
            cityDisplay: ci.nameTranslated || ci.name,
            countryName: co.name,
            countryDisplay: co.nameTranslated || co.name,
            countrySlug: co.slug,
            totalTours: Number(ci.totalTours ?? 0),
          })),
        );
        setAllRows(flat);
      } catch (e) {
        // Soft-fail: the popular list still works without the full dataset.
        console.warn("[MobileSearchSheet] citiesPerCountry fetch failed", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedLanguage]);

  // "RECHERCHES POPULAIRES" — first 6 featured cities in admin order. The
  // Redux slice (`state.cities.featured`) is already sorted by `order ASC`
  // by the homepage SSR, so we just slice — no resort. Sorting by
  // totalTours would override the editorial choice made in the admin.
  const popular = useMemo(() => (cities || []).slice(0, 6), [cities]);

  // Autocomplete kicks in at ≥3 chars (same threshold as legacy AutoComplete).
  // Matches against translated city name, translated country name, raw
  // country name. Accent-tolerant via NFD-strip.
  const trimmed = query.trim();
  const isSearching = trimmed.length >= 3;
  const matches = useMemo<FlatRow[]>(() => {
    if (!isSearching) return [];
    const q = normalize(trimmed);
    // Sort by totalTours desc so the richest results bubble up — keeps the
    // visible list useful when the query matches many ghost cities.
    return allRows
      .filter(
        (r) =>
          normalize(r.cityDisplay).includes(q) ||
          normalize(r.countryDisplay).includes(q) ||
          normalize(r.countryName).includes(q),
      )
      .sort((a, b) => b.totalTours - a.totalTours);
  }, [trimmed, isSearching, allRows]);

  // Group matches by country for the rendered list — keeps the visual
  // hierarchy of the legacy AutoComplete (country header + nested cities).
  const grouped = useMemo(() => {
    const map = new Map<
      string,
      { countryDisplay: string; countrySlug: string; cities: FlatRow[] }
    >();
    for (const r of matches) {
      const k = r.countrySlug || r.countryName;
      const bucket = map.get(k) || {
        countryDisplay: r.countryDisplay,
        countrySlug: r.countrySlug,
        cities: [],
      };
      bucket.cities.push(r);
      map.set(k, bucket);
    }
    return Array.from(map.values());
  }, [matches]);

  const citySlug = (cityName: string) =>
    cityName.trim().replace(/\s+/g, "-").toLowerCase();

  const goToCity = (row: FlatRow) => {
    onClose();
    router.push(`/${lang}/${currency}/city/${citySlug(row.cityName)}` as any);
  };

  const goToCountry = (slug: string) => {
    onClose();
    router.push(`/${lang}/${currency}/country/${slug}` as any);
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isSearching && matches.length > 0) {
      goToCity(matches[0]);
    } else if (!isSearching && popular.length > 0) {
      onClose();
      router.push(`/${lang}/${currency}/city/${(popular[0] as any).slug}` as any);
    } else {
      onClose();
      router.push(`/${lang}/${currency}/continents` as any);
    }
  };

  return (
    <div className="gdx">
      {/* Local CSS — keyframes for the slide-down + a focus reset for the
          input pill (browsers ship a blue ring by default and `outline:none`
          inline isn't enough on some platforms). */}
      <style>{`
        @keyframes gdx-search-sheet-slide-down {
          from { transform: translateY(-110%); opacity: 0; }
          to   { transform: translateY(0);     opacity: 1; }
        }
        @keyframes gdx-search-sheet-fade-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        .gdx-search-input,
        .gdx-search-input:focus,
        .gdx-search-input:focus-visible {
          outline: 0 !important;
          box-shadow: none !important;
          border: 0 !important;
          -webkit-appearance: none;
          appearance: none;
        }
      `}</style>

      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(20,40,30,.55)",
          zIndex: 1040,
          animation: "gdx-search-sheet-fade-in .2s ease both",
        }}
      />
      {/* Slide-down sheet */}
      <div
        role="dialog"
        aria-modal
        aria-label={t("mobile_home_search_sheet_label", {
          defaultValue: "Rechercher une visite",
        })}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          background: "white",
          zIndex: 1050,
          padding: "22px 16px 22px",
          boxShadow: "0 14px 30px rgba(0,0,0,.18)",
          maxHeight: "85vh",
          overflowY: "auto",
          borderRadius: 0,
          animation: "gdx-search-sheet-slide-down .3s cubic-bezier(.2,.8,.2,1) both",
        }}
      >
        <form
          onSubmit={onSubmit}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 16,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            aria-label={t("mobile_home_search_close", { defaultValue: "Fermer" })}
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              background: "var(--g-cream)",
              border: "none",
              display: "grid",
              placeItems: "center",
              color: "var(--g-burgundy)",
              fontSize: 16,
              fontWeight: 800,
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            ×
          </button>
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 14px",
              background: "var(--g-cream)",
              borderRadius: 999,
            }}
          >
            <span style={{ color: "var(--g-burgundy)" }} aria-hidden>
              🔍
            </span>
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="gdx-search-input"
              placeholder={t("mobile_home_search_placeholder", {
                defaultValue: "Une ville, un guide, une envie…",
              })}
              style={{
                flex: 1,
                background: "transparent",
                fontSize: 14,
                color: "var(--g-burgundy)",
                padding: 0,
              }}
            />
          </div>
        </form>

        {/* Empty / short query → "RECHERCHES POPULAIRES" (featured cities). */}
        {!isSearching && popular.length > 0 && (
          <>
            <div
              style={{
                fontSize: 10,
                color: "var(--g-ink-500)",
                fontWeight: 700,
                letterSpacing: "0.08em",
                marginBottom: 8,
              }}
            >
              {t("mobile_home_search_popular_eyebrow", {
                defaultValue: "RECHERCHES POPULAIRES",
              })}
            </div>
            {popular.map((c: any, i) => {
              const rawName = c?.entry?.data?.[0]?.title || c.name;
              const name = titleCase(rawName);
              // Prefer the localized country title from the CMS entry join
              // (e.g. "Frankreich" for FR + de_DE) — fall back to the raw
              // English `name` only when no translation is available.
              const country =
                c?.countryInfo?.entry?.data?.[0]?.title ||
                c?.countryInfo?.nameTranslated ||
                c?.countryInfo?.name ||
                "";
              const tours = c.totalTours ?? 0;
              const isLast = i === popular.length - 1;
              return (
                <Link
                  key={c.id}
                  href={`/${lang}/${currency}/city/${c.slug}` as any}
                  onClick={onClose}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    width: "100%",
                    padding: "12px 8px",
                    background: "transparent",
                    borderBottom: isLast ? "none" : "1px solid var(--g-line)",
                    textDecoration: "none",
                    color: "inherit",
                  }}
                >
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      background: "var(--g-cream-deep)",
                      display: "grid",
                      placeItems: "center",
                      flexShrink: 0,
                    }}
                    aria-hidden
                  >
                    <PinIcon />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: "var(--g-burgundy)",
                      }}
                    >
                      {name}
                      {country ? ` · ${country}` : ""}
                    </div>
                    {tours > 0 && (
                      <div style={{ fontSize: 10, color: "var(--g-ink-500)" }}>
                        {tours}{" "}
                        {t("mobile_home_search_tours_word", {
                          defaultValue: "tours",
                        })}
                      </div>
                    )}
                  </div>
                  <span
                    style={{ color: "var(--g-ink-500)", fontSize: 14 }}
                    aria-hidden
                  >
                    →
                  </span>
                </Link>
              );
            })}
          </>
        )}

        {/* Searching (≥3 chars) — full-catalog matches grouped by country.
            Same matching rules as the legacy AutoComplete (NFD-stripped
            contains on translated city / translated country / raw country
            name). */}
        {isSearching && grouped.length > 0 && (
          <div>
            <div
              style={{
                fontSize: 10,
                color: "var(--g-ink-500)",
                fontWeight: 700,
                letterSpacing: "0.08em",
                marginBottom: 8,
              }}
            >
              {t("mobile_home_search_results_eyebrow", {
                defaultValue: "RÉSULTATS",
              })}
            </div>
            {grouped.map(({ countryDisplay, countrySlug, cities: rows }) => (
              <div key={countrySlug || countryDisplay} style={{ marginBottom: 8 }}>
                <button
                  type="button"
                  onClick={() => goToCountry(countrySlug)}
                  style={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    background: "transparent",
                    border: "none",
                    padding: "8px 8px 4px",
                    fontSize: 11,
                    fontWeight: 800,
                    color: "var(--g-coral)",
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    cursor: "pointer",
                  }}
                >
                  {titleCase(countryDisplay)}
                </button>
                {rows.map((r) => (
                  <button
                    key={r.cityId}
                    type="button"
                    onClick={() => goToCity(r)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      width: "100%",
                      padding: "10px 8px",
                      background: "transparent",
                      border: "none",
                      textAlign: "left",
                      cursor: "pointer",
                    }}
                  >
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 8,
                        background: "var(--g-cream-deep)",
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                      }}
                      aria-hidden
                    >
                      <PinIcon />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: "var(--g-burgundy)",
                        }}
                      >
                        {titleCase(r.cityDisplay)}
                      </div>
                      {r.totalTours > 0 && (
                        <div style={{ fontSize: 10, color: "var(--g-ink-500)" }}>
                          {r.totalTours}{" "}
                          {t("mobile_home_search_tours_word", {
                            defaultValue: "tours",
                          })}
                        </div>
                      )}
                    </div>
                    <span
                      style={{ color: "var(--g-ink-500)", fontSize: 14 }}
                      aria-hidden
                    >
                      →
                    </span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}

        {isSearching && grouped.length === 0 && (
          <div
            style={{
              padding: "32px 8px",
              textAlign: "center",
              color: "var(--g-ink-500)",
              fontSize: 13,
            }}
          >
            {t("mobile_home_search_no_results", {
              defaultValue: "Aucun résultat. Essayez une autre ville.",
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileSearchSheet;
